import React, { useState, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { cn } from "@/lib/utils"
import { EyeIcon, EyeOffIcon, AlertCircleIcon, Loader2Icon } from "lucide-react"

interface LoginPageProps {
  /**
   * Logo text or component
   */
  logo?: string | React.ReactNode

  /**
   * Heading text
   */
  heading?: string

  /**
   * Subheading text
   */
  subheading?: string

  /**
   * Sign up link URL
   */
  signUpHref?: string

  /**
   * Forgot password link URL
   */
  forgotPasswordHref?: string

  /**
   * Login callback
   */
  onLogin?: (email: string, password: string, rememberMe: boolean) => Promise<void>

  /**
   * Show error state
   */
  showError?: boolean

  /**
   * Error message
   */
  errorMessage?: string

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Devise form action URL (from ERB)
   */
  formAction?: string

  /**
   * CSRF token (from ERB)
   */
  csrfToken?: string

  /**
   * Devise signup URL (from ERB)
   */
  signupUrl?: string

  /**
   * Devise forgot password URL (from ERB)
   */
  forgotPasswordUrl?: string

  /**
   * Server-side errors (from ERB flash)
   */
  errors?: string[]
}

export function LoginPage({
  logo = "Academician Startup",
  heading = "Welcome back",
  subheading = "Sign in to continue your learning",
  signUpHref,
  forgotPasswordHref,
  onLogin,
  showError: externalShowError,
  errorMessage: externalErrorMessage,
  className,
  formAction,
  csrfToken,
  signupUrl,
  forgotPasswordUrl,
  errors
}: LoginPageProps) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [internalError, setInternalError] = useState<string | null>(null)

  // Resolve URLs: prefer ERB-provided URLs, fall back to defaults
  const resolvedSignUpHref = signupUrl || signUpHref || "/users/sign_up"
  const resolvedForgotHref = forgotPasswordUrl || forgotPasswordHref || "/users/password/new"

  // Use server-side errors from flash, external error, or internal error
  const serverError = errors && errors.length > 0 ? errors[0] : null
  const showError = externalShowError ?? !!(serverError || internalError)
  const errorMessage = externalErrorMessage ?? serverError ?? internalError ?? "Invalid email or password. Please try again."

  const formRef = useRef<HTMLFormElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setInternalError(null)

    // Basic validation
    if (!email || !password) {
      setInternalError("Please fill in all fields")
      return
    }

    if (!email.includes("@")) {
      setInternalError("Please enter a valid email address")
      return
    }

    setIsLoading(true)

    // If formAction is provided (from Devise ERB), submit as a real form POST
    if (formAction) {
      formRef.current?.submit()
      return
    }

    try {
      if (onLogin) {
        await onLogin(email, password, rememberMe)
      }
    } catch (error) {
      setInternalError(
        error instanceof Error ? error.message : "Invalid email or password. Please try again."
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn("min-h-screen bg-muted/30 flex items-center justify-center p-4", className)}>
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-card border border-border rounded-lg shadow-lg p-8 space-y-6">
          {/* Logo */}
          <div className="text-center">
            {typeof logo === "string" ? (
              <h1 className="text-3xl font-bold text-[#666666]">{logo}</h1>
            ) : (
              logo
            )}
          </div>

          {/* Heading */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">{heading}</h2>
            <p className="text-sm text-muted-foreground">{subheading}</p>
          </div>

          {/* Error Alert */}
          {showError && (
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/50">
              <AlertCircleIcon className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Form */}
          <form ref={formRef} onSubmit={handleSubmit} action={formAction} method="post" className="space-y-4">
            {csrfToken && <input type="hidden" name="authenticity_token" value={csrfToken} />}
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="user[email]"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className={cn(
                  showError && "border-destructive focus-visible:ring-destructive"
                )}
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="user[password]"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className={cn(
                    "pr-10",
                    showError && "border-destructive focus-visible:ring-destructive"
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOffIcon className="w-4 h-4" />
                  ) : (
                    <EyeIcon className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Hidden field for remember_me (Devise expects user[remember_me]) */}
            <input type="hidden" name="user[remember_me]" value={rememberMe ? "1" : "0"} />

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked === true)}
                  disabled={isLoading}
                />
                <Label
                  htmlFor="remember"
                  className="text-sm font-normal cursor-pointer"
                >
                  Remember me
                </Label>
              </div>
              <a
                href={resolvedForgotHref}
                className="text-sm font-medium text-primary hover:underline"
                tabIndex={isLoading ? -1 : 0}
              >
                Forgot password?
              </a>
            </div>

            {/* Sign In Button */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          {/* Sign Up Link */}
          <div className="text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <a
              href={resolvedSignUpHref}
              className="font-medium text-primary hover:underline"
            >
              Sign up
            </a>
          </div>
        </div>

        {/* Helper Text (Optional) */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          By signing in, you agree to our{" "}
          <a href="/terms" className="underline hover:text-foreground">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy" className="underline hover:text-foreground">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  )
}